import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { MasterService } from 'src/app/services/master.service';
import { MessageService } from 'src/app/services/message.service';
import { StoreService } from 'src/app/services/store.service';
import * as languageLibrary from '../../../../services/language'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
    selector: 'inventory-print-center',
    templateUrl: './inventory-print-center.component.html',
    styleUrls: ['./inventory-print-center.component.scss'],
    standalone: false
})
export class InventoryPrintCenterComponent implements OnInit {
    // define the inventory id
    @Input() inventoryId: number = 0

    // save the language
    @Input() language: string = 'EN'

    private lotId: number | null = null

    private lotAddress: Object | null = null

    // A list of available forms for the sale.
    protected forms: Object[] = []

    // A grouping of the existing forms suitable for display in the UI.
    protected groups: Object = {}

    // set all words
    protected words = languageLibrary.language

    protected pdf: any = null
    protected setupValuesString: string | null = null
    protected selectedForm: any;

    get groupKeys() { return this.groups ? Object.keys(this.groups) : [] }
    public formKeys(group) { return (this.groups && this.groups[group]) ? Object.keys(this.groups[group]) : [] }
    public copies(group, form) { return (this.groups && this.groups[group] && this.groups[group][form]) ? this.groups[group][form][0].copies : 0 }
    public revision(group, form) { return (this.groups && this.groups[group] && this.groups[group][form]) ? this.groups[group][form][0].revision : 0 }
    public allowSetupValueChanges(group, form) { return (this.groups && this.groups[group] && this.groups[group][form]) ? this.groups[group][form][0].allowSetupValueChanges : 0 }
    public setupValues(group, form) { return (this.groups && this.groups[group] && this.groups[group][form]) ? this.groups[group][form][0].setupValues : 0 }
    public form(group, form) { return (this.groups && this.groups[group] && this.groups[group][form]) ? this.groups[group][form][0] : 0 }

    constructor(private ms: MessageService, private master: MasterService, private store: StoreService, private sanitizer: DomSanitizer) { }

    ngOnInit() {
        this.lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)
        this.discoverForms()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.inventoryId) {
            this.discoverForms()
        }
    }

    /**
    * Query for or used cached sale lot state.
    * @returns A two-character state abbreviation for the associated sale lot
    */
    private determineLotState = async () => {
        if (!this.lotId || (this.lotId <= 0)) {
            return null
        }

        if (this.lotAddress) {
            return this.lotAddress['state']
        }

        const res = await this.master.getAsync(`lots/${this.lotId}`)
        if (!res || !res.data || !res.data['lotDetails'] || !res.data['lotDetails']['contact'] ||
            !res.data['lotDetails']['contact']['contactAddresses'] || (res.data['lotDetails']['contact']['contactAddresses'].length <= 0)) {
            return null
        }

        const addy = res.data['lotDetails']['contact']['contactAddresses'].find(addy => addy.primaryAddress == 1)
        if (!addy || !addy.state) {
            return null
        }

        this.lotAddress = addy
        return this.lotAddress['state']
    }

    // todo: get reports
    public discoverForms = async () => {
        if (!this.inventoryId || (this.inventoryId <= 0)) {
            return
        }

        const lotState = await this.determineLotState()
        let stateParam = ''
        if (lotState && (lotState.length > 0)) {
            stateParam = `&state=${lotState}`
        }

        let inventoryIdParam = ''
        if (this.inventoryId && (this.inventoryId > 0)) {
            inventoryIdParam = `&inventoryId=${this.inventoryId}`
        }

        const res = await this.master.getAsync(`forms?lotId=${this.lotId}&panel=forms&module=inventory${stateParam}${inventoryIdParam}`)

        if (!res || (res['status'] !== 200)) {
            this.ms.sendMessage("alert", { type: "danger", text: res ? res['data'].error : this.words[this.language]['apiNoResponse'] })
            return
        }

        this.forms = res.data.forms
        this.groups = this.grouped()
    }
    /**
    * Group the current forms list by category and name 
    * so they are ready for display in the UI.
    */
    public grouped = (): object => {
        const groups = {}

        if (!this.forms || (this.forms.length <= 0)) {
            return
        }

        for (const form of this.forms) {
            if (!groups[form['category']]) {
                groups[form['category']] = {}
            }

            if (!groups[form['category']][form['name']]) {
                groups[form['category']][form['name']] = []
            }

            groups[form['category']][form['name']].push({
                id: parseInt(form['id']),
                formId: parseInt(form['formId']),
                producer: form['producer'],
                copies: parseInt(form['copies']),
                selected: true,
                revision: form['revision'],
                allowSetupValueChanges: form['allowSetupValueChanges'],
                setupValues: form['setupValues'],
                name: form['name'],
                managed: form['managed'],
            })
        }

        return groups
    }
    /**
    * 
    * @param group 
    * @param form 
    */
    public view = (group, form) => {
        if (!this.groups || !group || !form) {
            return
        }

        if (!this.groups[group] || !this.groups[group][form] || (this.groups[group][form].length <= 0)) {
            return
        }

        const item = this.groups[group][form][0]

        this.download(item.id, form)
    }

    /**
    * Download the given form from the API.
    * @param id Unique internal identity of the form to construct and download.
    * @param name The name of the form to download.
    */
    public download = (id: number, name: string) => {
        if (!this.inventoryId || this.inventoryId <= 0) {
            return
        }

        if (!id || id <= 0) {
            return
        }

        this.pdf = null
        const payload = {
            forms: [
                {
                    formId: id,
                    module: 'inventory',
                    xChange: null,
                    yChange: null
                }
            ]
        }

        this.master.post(`forms?lotId=${this.lotId}&inventoryId=${this.inventoryId}`, payload, (meta) => {
            if (!meta || !meta.status || !meta.data || !meta.data.url) {
                this.ms.sendMessage("alert", { type: "danger", text: (meta && meta.error) ? meta.error : this.words[this.language]['apiNoResponse'] })
                return
            }

            const url: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(meta.data.url)
            if (!url) {
                this.ms.sendMessage("alert", { type: "danger", text: "Pdf meta data url is empty or null." })
                return
            }

            this.pdf = url

            const btn = document.getElementById('btn-view-pdf-modal') as HTMLButtonElement
            if (!btn) {
                return
            }

            btn.click()
        })
    }

    public openModal(group, form) {
        this.setupValuesString = this.setupValues(group, form)
        this.selectedForm = this.form(group, form)
        document.getElementById("btn-view-dynamicSetupForm-modal").click()
    }
}
